import stringMatch from '@utils/string-match';

const CATEGORY_ORDER = { Knowledge: 1, Skill: 2, Behaviour: 3 };

export const COMPETENCY_SORT_MODES = {
  DEFAULT: 'default',
  WEAKEST: 'weakest',
  STRONGEST: 'strongest',
};

const EMPTY_LATEST_SCORE = { score: 0, submitted_at: '' };

/**
 * Score entry with the latest submitted_at. Empty/missing scores return score 0.
 * @param {Array} [scores]
 * @returns {{ score: Number, submitted_at: String }}
 */
export function getLatestScore(scores) {
  if (!scores?.length) return EMPTY_LATEST_SCORE;

  return scores.reduce((latest, score) => {
    if (!latest || score.submitted_at > latest.submitted_at) {
      return score;
    }
    return latest;
  });
}

function compareByCategoryThenLabel(a, b) {
  if (CATEGORY_ORDER[a.category] !== CATEGORY_ORDER[b.category]) {
    return CATEGORY_ORDER[a.category] - CATEGORY_ORDER[b.category];
  }

  const aNumber = parseInt(a.label.substring(1), 10);
  const bNumber = parseInt(b.label.substring(1), 10);
  return aNumber - bNumber;
}

/**
 * Sort competencies by mode: default (K → S → B then label), weakest, or strongest by latest score.
 * Equal scores keep default category + label order.
 * @param {Array} competencies
 * @param {String} [mode]
 * @returns {Array}
 */
export function sortCompetencies(competencies, mode = COMPETENCY_SORT_MODES.DEFAULT) {
  if (!competencies?.length) return [];

  return [...competencies].sort((a, b) => {
    if (mode === COMPETENCY_SORT_MODES.WEAKEST || mode === COMPETENCY_SORT_MODES.STRONGEST) {
      const aScore = getLatestScore(a?.scores).score ?? 0;
      const bScore = getLatestScore(b?.scores).score ?? 0;
      if (aScore !== bScore) {
        return mode === COMPETENCY_SORT_MODES.WEAKEST
          ? aScore - bScore
          : bScore - aScore;
      }
    }

    return compareByCategoryThenLabel(a, b);
  });
}

/**
 * Filter competencies by category predicate and free-text search.
 * @param {Array} competencies
 * @param {Object} options
 * @param {String} [options.text]
 * @param {Function} [options.categoryFilter]
 * @param {Function} [options.parseTimestamp] - formats latestScore for text match
 * @returns {Array}
 */
export function filterCompetencyList(competencies, {
  text = '',
  categoryFilter,
  parseTimestamp,
} = {}) {
  if (!competencies?.length) return [];

  return competencies.filter(item => {
    if (categoryFilter && !categoryFilter(item)) {
      return false;
    }
    if (!text) {
      return true;
    }
    const latestScoreText = parseTimestamp ? parseTimestamp(item.latestScore) : item.latestScore;
    return stringMatch(item.description, text, true)
      || stringMatch(item.label, text, true)
      || stringMatch(item.category, text, true)
      || stringMatch(latestScoreText, text, true);
  });
}
